import { isUrl } from '@lib/string';
import axios from 'axios';
import cookie from 'js-cookie';
import * as publicRuntimeConfig from '../lib/config';

export interface IResponse<T> {
  status: number;
  data: T;
}

export const TOKEN = 'token';

export abstract class APIRequest {
  static API_ENDPOINT: string = null;

  // Initialize API endpoint when class is loaded
  static {
    if (typeof window !== 'undefined') {
      // Client-side initialization
      this.API_ENDPOINT = process.env.NEXT_PUBLIC_API_ENDPOINT || 'https://api.talentiq.studio';
    }
  }

  getBaseApiEndpoint() {
    const { API_ENDPOINT } = APIRequest;
    if (API_ENDPOINT) return API_ENDPOINT;

    // Determine if we're running on server or client
    const isServer = typeof window === 'undefined';

    let apiEndpoint: string | undefined;

    if (isServer) {
      // Server-side: Use server endpoint first, then fallback to public endpoint
      apiEndpoint = process.env.API_SERVER_ENDPOINT || process.env.API_ENDPOINT;
    } else {
      // Client-side: Use public endpoint (should be available after build)
      apiEndpoint = process.env.NEXT_PUBLIC_API_ENDPOINT;
    }

    if (!apiEndpoint) {
      // Fallback to default API endpoint
      console.warn(`API_ENDPOINT not configured, using fallback. Please set ${isServer ? 'API_ENDPOINT or API_SERVER_ENDPOINT' : 'NEXT_PUBLIC_API_ENDPOINT'} environment variable.`);
      apiEndpoint = 'https://api.talentiq.studio';
    }

    // Fallback to config if still not set
    return apiEndpoint || publicRuntimeConfig.API_ENDPOINT;
  }

  request(
    url: string,
    method?: string,
    body?: any,
    headers?: { [key: string]: string }
  ): Promise<IResponse<any>> {
    const verb = (method || 'get').toUpperCase();

    const updatedHeader = {
      'Content-Type': 'application/json',
      // TODO - check me
      Authorization: cookie.get(TOKEN) || '',
      ...(headers || {})
    };
    const baseApiEndpoint = this.getBaseApiEndpoint();

    return axios(isUrl(url) ? url : `${baseApiEndpoint}${url}`, {
      method: verb,
      headers: updatedHeader,
      data: body ? JSON.stringify(body) : undefined,
      withCredentials: true,
      timeout: 10000, // 10 second timeout (reduced from 30)
      // For server-side requests, use keep-alive for better performance
      ...(typeof window === 'undefined' && {
        httpAgent: require('http').Agent({ keepAlive: true }),
        httpsAgent: require('https').Agent({ keepAlive: true })
      })
    })
      .then((resp) => resp.data)
      .catch((error) => {
        const { response } = error;
        if (response?.status === 401) {
          if (typeof window !== 'undefined') {
            window.location.href = '/auth/login';
          }
          throw new Error('Forbidden in the action!');
        }
        // Handle case where response might be undefined (network errors, etc.)
        if (response?.data) {
          throw response.data;
        }
        // Fallback for errors without response (network errors, timeouts, etc.)
        throw new Error(error.message || 'Network error occurred');
      });
  }

  buildUrl(baseUrl: string, params?: { [key: string]: any }) {
    if (!params) {
      return baseUrl;
    }

    const queryString = Object.keys(params)
      .map((k) => {
        if (Array.isArray(params[k])) {
          return params[k].map((param) => `${encodeURI(k)}=${encodeURI(param)}`)
            .join('&');
        }
        return `${encodeURI(k)}=${encodeURI(params[k])}`;
      })
      .join('&');
    return `${baseUrl}?${queryString}`;
  }

  get(url: string, headers?: { [key: string]: string }) {
    return this.request(url, 'get', null, headers);
  }

  post(url: string, data?: any, headers?: { [key: string]: string }) {
    return this.request(url, 'post', data, headers);
  }

  put(url: string, data?: any, headers?: { [key: string]: string }) {
    return this.request(url, 'put', data, headers);
  }

  del(url: string, data?: any, headers?: { [key: string]: string }) {
    return this.request(url, 'delete', data, headers);
  }

  upload(
    url: string,
    files: {
      file: File;
      fieldname: string;
    }[],
    options: {
      onProgress: Function;
      customData?: any;
      method?: string;
    } = {
      onProgress() { },
      method: 'POST'
    },
    pauseButton: any = null
  ) {
    const baseApiEndpoint = this.getBaseApiEndpoint();
    const uploadUrl = isUrl(url) ? url : `${baseApiEndpoint}${url}`;
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest();

      req.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          options.onProgress({
            percentage: (event.loaded / event.total) * 100
          });
        }
      });

      req.addEventListener('load', () => {
        const success = req.status >= 200 && req.status < 300;
        const { response } = req;
        if (!success) {
          return reject(response);
        }
        return resolve(response);
      });

      req.upload.addEventListener('error', () => {
        reject(req.response);
      });

      const formData = new FormData();
      files.forEach((f) => formData.append(f.fieldname, f.file, f.file.name));
      if (options.customData) {
        Object.keys(options.customData).forEach(
          (fieldname) => {
            if (typeof options.customData[fieldname] !== 'undefined' && !Array.isArray(options.customData[fieldname])) formData.append(fieldname, options.customData[fieldname]);
            if (typeof options.customData[fieldname] !== 'undefined' && Array.isArray(options.customData[fieldname])) {
              if (options.customData[fieldname].length) {
                for (let i = 0; i < options.customData[fieldname].length; i += 1) {
                  formData.append(fieldname, options.customData[fieldname][i]);
                }
              }
            }
          }
        );
      }

      req.responseType = 'json';
      req.open(options.method || 'POST', uploadUrl);

      const token: any = cookie.get(TOKEN);
      req.setRequestHeader('Authorization', token || '');
      req.send(formData);
      if (pauseButton) {
        pauseButton.addEventListener('click', () => {
          req.abort();
          reject();
        });
      }
    });
  }
}
